import { GameState, GameHistory, GameConfig, GameMode } from './gameState';
import { jsonDateReviver, getDateString, getNormalizedDate } from './dateUtils';
import { applyStorageMigrations } from './storageMigrations';

// Complete snapshot of all localStorage data
export interface AllStorageData {
  games: Record<string, GameState>;  // All game states keyed by "gameState:mode:date"
  histories: Record<GameMode, GameHistory>;  // Daily/Random/Encore histories
  config: GameConfig;
}

const GAME_CONFIG_KEY = 'gameConfig';
const ENCORE_GAME_STORAGE_KEY = 'gameState:encore';

const isValidDate = (date: Date | null | undefined): date is Date => {
  return date instanceof Date && !Number.isNaN(date.getTime());
};

const getEncoreGameFromStorage = (games: Record<string, GameState>): GameState | null => {
  return games[ENCORE_GAME_STORAGE_KEY] || null;
};

function normalizeLoadedConfig(config: GameConfig, games: Record<string, GameState>): GameConfig {
  const today = getNormalizedDate();
  const todayString = getDateString(today);

  switch (config.mode) {
    case GameMode.DAILY:
      // Always point Daily mode at today's puzzle when opening/reloading.
      return { mode: GameMode.DAILY, date: today };
    case GameMode.ENCORE: {
      const encoreGame = getEncoreGameFromStorage(games);
      const encoreDate = encoreGame?.targetWordDate;
      const isEncoreForToday = isValidDate(encoreDate) && getDateString(encoreDate) === todayString;

      // Stale/missing Encore should fall back to today's Daily.
      if (!isEncoreForToday) {
        return { mode: GameMode.DAILY, date: today };
      }

      return { mode: GameMode.ENCORE, date: null };
    }
    case GameMode.ARCHIVE:
      if (isValidDate(config.date)) {
        return { mode: GameMode.ARCHIVE, date: getNormalizedDate(config.date) };
      }
      return { mode: GameMode.DAILY, date: today };
    case GameMode.RANDOM:
      return { mode: GameMode.RANDOM, date: null };
    default:
      return { mode: GameMode.DAILY, date: today };
  }
}

/**
 * Private helper to load game config from localStorage
 * This replicates the logic from loadGameConfig in gameState.ts
 */
function loadGameConfigFromStorage(): GameConfig {
  const rawConfig = localStorage.getItem(GAME_CONFIG_KEY);
  if (rawConfig) {
    try {
      return JSON.parse(rawConfig, jsonDateReviver) as GameConfig;
    } catch (e) {
      console.error('Error parsing saved game config:', e);
      // Fall through to default config if parsing fails.
    }
  }

  return { mode: GameMode.DAILY, date: getNormalizedDate() };
}

/**
 * Load ALL data from localStorage once on app initialization
 * This is the only place that should read from localStorage on startup
 */
export function loadAllStorageData(): AllStorageData {
  const games: Record<string, GameState> = {};
  const histories: Record<GameMode, GameHistory> = {
    [GameMode.DAILY]: {},
    [GameMode.RANDOM]: {},
    [GameMode.ENCORE]: {},
    [GameMode.ARCHIVE]: {}
  };

  // Iterate through all localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    try {
      const value = localStorage.getItem(key);
      if (!value) continue;

      // Load game states
      if (key.startsWith('gameState:')) {
        games[key] = JSON.parse(value, jsonDateReviver) as GameState;
      }

      // Load histories
      if (key.startsWith('gameHistory:')) {
        const mode = key.split(':')[1] as GameMode;
        histories[mode] = JSON.parse(value) as GameHistory;
      }
    } catch (e) {
      console.error(`Error parsing localStorage key ${key}:`, e);
    }
  }

  // Load config and run storage migrations before app initialization.
  const rawConfig = loadGameConfigFromStorage();
  const migrationResult = applyStorageMigrations({
    games,
    histories,
    config: rawConfig
  });

  migrationResult.writes.forEach(({ key, value }) => {
    localStorage.setItem(key, JSON.stringify(value));
  });

  const config = normalizeLoadedConfig(migrationResult.config, migrationResult.games);

  return {
    games: migrationResult.games,
    histories: migrationResult.histories,
    config
  };
}

/**
 * Debounced write system to prevent race conditions
 */
class StorageWriter {
  private writeQueue: Map<string, any> = new Map();
  private writeTimer: NodeJS.Timeout | null = null;
  private readonly DEBOUNCE_MS = 100;

  /**
   * Queue a write to localStorage
   * Multiple rapid calls will be batched together
   */
  queueWrite(key: string, value: any): void {
    this.writeQueue.set(key, value);

    if (this.writeTimer) clearTimeout(this.writeTimer);

    this.writeTimer = setTimeout(() => {
      this.flush();
    }, this.DEBOUNCE_MS);
  }

  /**
   * Immediately flush all pending writes
   */
  flush(): void {
    this.writeQueue.forEach((value, key) => {
      localStorage.setItem(key, JSON.stringify(value));
    });
    this.writeQueue.clear();
  }

  /**
   * Flush on page unload to prevent data loss
   */
  setupUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }
}

export const storageWriter = new StorageWriter();
storageWriter.setupUnloadHandler();
